import { ChevronRight } from 'lucide-react'
import { cn } from '@teach/ui'
import { formatCreditsOrDash } from '@/features/ai/lib/money'
import { formatDateOrDash } from '@/lib/formatDate'
import type { CreditsHistoryItem } from '@/features/ai/creditsApi'
import { historyRowView } from './lib/historyRow'
import { HistoryThumb } from './HistoryThumb'
import { CreditIcon } from '@/components/common/CreditIcon'
import { uiLocale, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  item: CreditsHistoryItem
  onOpen: (item: CreditsHistoryItem) => void
}

const DATE = new Map<string, Intl.DateTimeFormat>()

/** Формат на языке интерфейса; один экземпляр на локаль, а не на строку. */
const dateFormat = (): Intl.DateTimeFormat => {
  const locale = uiLocale()
  const cached = DATE.get(locale)
  if (cached) return cached
  const created = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    // Время: десяток списаний за один день иначе неотличимы.
    hour: '2-digit',
    minute: '2-digit',
  })
  DATE.set(locale, created)
  return created
}

/** Колонки — общие со строкой заголовков (`HistoryHeader`), чтобы не разъехались.
 *  Узкий экран — базовые классы, широкий — `sm:` (`max-*` в проекте не работают). */
export const HISTORY_TIME_COL = 'hidden w-36 shrink-0 sm:block'
export const HISTORY_AMOUNT_COL = 'flex shrink-0 items-center justify-end gap-1.5 sm:w-28'
const ROW = 'flex items-center gap-3 rounded-lg px-2 py-3 sm:gap-4'

/**
 * Строка движения. Кликабельна ровно тогда, когда есть что показать: ховер и
 * стрелка над пополнением были бы фальшивой affordance.
 */
export const HistoryRow = ({ item, onOpen }: Props) => {
  const m = useMessages(messages)
  const view = historyRowView(item)
  const time = formatDateOrDash(item.createdAt, dateFormat())
  const body = (
    <>
      <HistoryThumb url={item.detail?.imageUrl} size="row" />
      {/* Только подпись: `description` — служебная строка бэка («AI credits
          purchase: credits_200»), не для автора. */}
      {/* На телефоне колонке времени места нет — время уходит второй строкой под подпись. */}
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="truncate text-base font-medium">{view.label}</span>
        <span className="text-xs tabular-nums text-muted-foreground sm:hidden">{time}</span>
      </span>
      <span
        className={cn(HISTORY_TIME_COL, 'text-left text-sm tabular-nums text-muted-foreground')}
      >
        {time}
      </span>
      <span
        className={cn(
          HISTORY_AMOUNT_COL,
          'text-base font-semibold tabular-nums',
          view.tone === 'positive' ? 'text-success' : null,
          view.tone === 'neutral' ? 'text-muted-foreground' : null,
        )}
      >
        {`${view.sign}${formatCreditsOrDash(item.amount)}`}
        <CreditIcon muted className="size-3.5" />
      </span>
    </>
  )

  if (!view.canExpand)
    return (
      <div className={ROW}>
        {body}
        {/* Место стрелки — чтобы колонки сумм не съезжали у строк без подробностей. */}
        <span className="size-4 shrink-0" />
      </div>
    )

  return (
    <button
      type="button"
      aria-label={m.details(view.label)}
      onClick={() => onOpen(item)}
      className={cn(ROW, 'w-full transition-colors hover:bg-foreground/5')}
    >
      {body}
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </button>
  )
}
